// src/features/leases/components/DocumentRow.jsx
//
// LSE7 — single lease document row. Renders file name with document-type
// pill, execution date (or "Unexecuted"), optional notes, and a provenance
// badge from field_sources (spacebase/manual/computed). Follows the same
// visual language as EditableLeaseField but read-only.
//
// LEASE-DOCS-COLLAPSE: each row is collapsed to a 1-line summary by default.
// A right-aligned chevron toggles the detail block (execution date, uploaded
// date, notes). Rows with no expandable content (no execution date, no
// uploaded date, no notes) render flat with no chevron, matching
// CriticalDateRow's hasBody pattern.

import React, { useState } from "react";
import { T } from "../../../app/tokens.js";
import { ProvenanceBadge } from "./ProvenanceBadge.jsx";

const TYPE_PILL_BG = {
  Lease: T.blueSoft, Sublease: T.blueSoft, "Master Lease": T.blueSoft,
  Amendment: T.pillAmberBg, Consent: T.pillAmberBg,
  "Serviced Office Agreement": T.provenanceSpacebaseBg, "Memorandum of Understanding": T.provenanceSpacebaseBg,
  "Renewal Confirmation": T.pillGreenBg, "Letter of Intent": T.pillGreenBg,
  Assignment: T.pillRedBg, "Lease Termination": T.pillRedBg,
  "Possession Letter": T.provenancePdfBg, Estoppel: T.provenancePdfBg,
  Invoice: T.warmBeige, "Additional Rent Notice": T.warmBeige,
  "Commencement Letter": T.provenanceSpacebaseBg, Proposal: T.provenanceSpacebaseBg,
  "Side Letter": T.pillAmberBg, Correspondence: T.warmBeige,
  "Supporting Document": T.warmBeige, Other: T.warmBeige,
};
// INC-06: Lease/Sublease/Master/SAA/MOU/Commencement/Proposal → T.spacebaseBlue.
const TYPE_PILL_FG = {
  Lease: T.spacebaseBlue, Sublease: T.spacebaseBlue, "Master Lease": T.spacebaseBlue,
  Amendment: T.expirationAmber, Consent: T.expirationAmber,
  "Serviced Office Agreement": T.spacebaseBlue, "Memorandum of Understanding": T.spacebaseBlue,
  "Renewal Confirmation": T.forestGreen, "Letter of Intent": T.forestGreen,
  Assignment: T.breakRed, "Lease Termination": T.breakRed,
  "Possession Letter": T.provenancePdf, Estoppel: T.provenancePdf,
  Invoice: T.mid, "Additional Rent Notice": T.mid,
  "Commencement Letter": T.spacebaseBlue, Proposal: T.spacebaseBlue,
  "Side Letter": T.expirationAmber, Correspondence: T.mid,
  "Supporting Document": T.mid, Other: T.mid,
};

export function DocumentRow({ doc }) {
  const {
    fileName,
    documentType,
    executionDate,
    notes,
    fieldSources,
    storageUrl,
    dateUploaded,
  } = doc;

  // Provenance: if any diffable field was touched manually, badge as manual.
  const source = fieldSources?.documentType || fieldSources?.fileName ||
    (fieldSources ? "spacebase" : undefined);

  const bg = TYPE_PILL_BG[documentType] || TYPE_PILL_BG.Other;
  const fg = TYPE_PILL_FG[documentType] || TYPE_PILL_FG.Other;

  // hasBody: true when any detail content is present. Rows with no body render
  // flat (no chevron). Matches CriticalDateRow's hasBody pattern.
  const hasBody = !!(executionDate || dateUploaded || notes);
  const [collapsed, setCollapsed] = useState(true);

  return (
    <div
      data-testid="document-row"
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        padding: "8px 0",
        borderBottom: `1px solid ${T.border}`,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* ── Summary line ──────────────────────────────────────────────── */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "nowrap" }}>
          <span
            style={{
              fontSize: T.fsBase,
              fontWeight: T.fwSemibold,
              color: T.dark,
              wordBreak: "break-word",
              flex: 1,
              minWidth: 0,
            }}
          >
            {storageUrl ? (
              <a href={storageUrl} target="_blank" rel="noreferrer" style={{ color: T.blue, textDecoration: "none" }}>
                {fileName}
              </a>
            ) : (
              fileName
            )}
          </span>
          <span
            style={{
              fontSize: T.fsXxs,
              fontWeight: T.fwBold,
              textTransform: "uppercase",
              letterSpacing: 0.4,
              padding: "1px 6px",
              borderRadius: 4,
              background: bg,
              color: fg,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            {documentType}
          </span>
          {source && <ProvenanceBadge source={source} />}
          {hasBody && (
            <button
              type="button"
              data-testid="doc-expand-toggle"
              aria-expanded={!collapsed}
              onClick={() => setCollapsed((c) => !c)}
              title={collapsed ? "Show details" : "Hide details"}
              style={{
                background: "none",
                border: "none",
                padding: "0 2px",
                cursor: "pointer",
                color: T.mid,
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "center",
                marginLeft: "auto",
                flexShrink: 0,
              }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                style={{ transform: collapsed ? undefined : "rotate(180deg)" }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
          )}
        </div>

        {/* ── Detail block — only when hasBody and expanded ─────────────── */}
        {hasBody && !collapsed && (
          <>
            <div style={{ marginTop: 2, fontSize: T.fsSm, color: T.mid, display: "flex", gap: 12, flexWrap: "wrap" }}>
              <span data-testid="doc-execution-date">
                {executionDate
                  ? new Date(executionDate).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
                  : "Unexecuted"}
              </span>
              {dateUploaded && (
                <span>Uploaded {new Date(dateUploaded).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}</span>
              )}
            </div>
            {notes && (
              <div style={{ marginTop: 4, fontSize: T.fsSm, color: T.mid, whiteSpace: "pre-wrap" }}>
                {notes}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
